import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { width, space } from 'styled-system';
import { SmallLoader } from 'shared/components';
import _ from 'lodash';
import { useStores } from 'src/hooks/use-stores';
import { Button } from 'src/components/button';
import { VisuallyHidden } from 'src/components/visually-hidden';
import { validateImage, ImageValidationConfig } from './image-validations';

const ImageUploadField: React.FC<{
  fieldId: string;
  helperText: string;
  image: string;
  handleUpload: (url: string | null) => void;
  validationConfig?: ImageValidationConfig;
}> = ({ fieldId, helperText, image, handleUpload, validationConfig }) => {
  const imageRef = useRef<HTMLInputElement>(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [selectedFilename, setSelectedFilename] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { UI } = useStores();

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!event.target.files?.[0]) {
      return;
    }

    const file = event.target.files[0];

    if (file.size > 2_000_000) {
      UI.showErnie('Your file is too large. Please upload a file smaller than 2MB', 'danger');
      if (imageRef.current) {
        imageRef.current.value = '';
      }
      return;
    }

    const allowedFileTypes = ['png'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!_.includes(allowedFileTypes, fileExtension)) {
      UI.showErnie(`Supported formats: .${allowedFileTypes.join(', .')}`, 'danger');
      if (imageRef.current) {
        imageRef.current.value = '';
      }
      return;
    }

    const reader = new FileReader();
    reader.onload = async (readEvent) => {
      const imageDataUrl = readEvent.target?.result as string;

      if (validationConfig) {
        const validation = await validateImage(imageDataUrl, validationConfig);
        if (!validation.isValid) {
          UI.showErnie(validation.error ?? 'Image validation failed', 'danger');
          if (imageRef.current) {
            imageRef.current.value = '';
          }
          return;
        }
      }

      setSelectedFilename(file.name);
      setSelectedImage(imageDataUrl);
      handleUpload(imageDataUrl);
    };
    reader.readAsDataURL(file);
  };

  const displayedImage = selectedImage ?? image;

  return (
    <>
      <ImageContainer width='200px' mx='10px' loading={loadingImage}>
        {displayedImage && loadingImage && <SmallLoader />}
        {displayedImage && <img src={displayedImage} onLoad={() => setLoadingImage(false)} />}
        {!displayedImage && (
          <EmptyImage>
            <MainCopy>No image</MainCopy>
          </EmptyImage>
        )}
      </ImageContainer>
      <Info>
        {selectedImage && (
          <div>
            <FileHeader>File</FileHeader>
            <FileName>{selectedFilename ?? _.last(image.split('/'))}</FileName>
          </div>
        )}
        <VisuallyHidden>
          <input ref={imageRef} type='file' onChange={onChange} accept='image/png' id={fieldId} />
        </VisuallyHidden>
        <FileInput htmlFor={fieldId}>
          <Button onClick={() => imageRef.current?.click()}>Upload new image</Button>
          <small>{helperText}</small>
        </FileInput>
      </Info>
    </>
  );
};

const EmptyImage = styled.div`
  width: 100%;
  height: 130px;
  background-color: #e7ebef;
  border-radius: 4px;
  border: 1px solid #d0d5db;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const MainCopy = styled.p`
  color: #7c8ea1;
  font-size: 16px;
  font-weight: bold;
  margin-top: 10px;
  margin-bottom: 8px;
`;

const ImageContainer = styled.div`
  ${width}
  ${space}
  margin: 5px 10px 5px 0;

  img {
    display: ${(props: { loading: boolean }) => (props.loading ? 'none' : 'block')};
  }
`;

const Info = styled.div`
  width: 202px;
  text-align: left;
`;

const FileInput = styled.label`
  font-size: 13px;
  color: #4597e0;
  cursor: pointer;
  position: relative;

  button {
    // Overrides the super admin color, which is !important
    color: #fff !important;
  }

  input {
    position: absolute;
    left: 0;
    opacity: 0;
    cursor: pointer;
    text-decoration: underline;
  }

  small {
    display: block;
    margin-top: 5px;
  }
`;

const FileHeader = styled.p`
  font-size: 12px;
  font-weight: bold;
  color: #a8aeb3;
  text-transform: uppercase;
  margin-bottom: 7px;
`;

const FileName = styled.p`
  font-size: 13px;
  color: #6d747b;
  margin-bottom: 18px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export default ImageUploadField;
